import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CartCtx = createContext(null);

// Cart line key = productId + optional variantId (identifies the SKU-per-variant).
const lineKey = (pid, vid) => vid ? `${pid}::${vid}` : String(pid);

// ─── Guest cart (localStorage) ───────────────────────────────────────────
// Structure mirrors the server cart shape so downstream UI code (Cart page,
// MiniCart, Checkout summary) works identically for guests and signed-in users.
const GUEST_KEY = "bb_guest_cart_v1";

const emptyGuestCart = () => ({ items: [], totals: { subtotal: 0, tax: 0, discount: 0, total_before_delivery: 0 } });

const readGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    if (!raw) return emptyGuestCart();
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return emptyGuestCart();
    return { ...emptyGuestCart(), ...parsed, items: parsed.items };
  } catch { return emptyGuestCart(); }
};

const writeGuestCart = (cart) => {
  try {
    if (!cart || (cart.items || []).length === 0) localStorage.removeItem(GUEST_KEY);
    else localStorage.setItem(GUEST_KEY, JSON.stringify(cart));
  } catch { /* noop — private mode / quota */ }
};

const clearGuestCart = () => { try { localStorage.removeItem(GUEST_KEY); } catch { /* noop */ } };

const recomputeGuestTotals = (items) => {
  const subtotal = items.reduce((s, it) => s + Number(it.price_snapshot || 0) * Number(it.qty || 0), 0);
  return { subtotal, tax: 0, discount: 0, total_before_delivery: subtotal };
};

const snapshotFromProduct = (p, variantId) => {
  if (!p) return {};
  return {
    name: p.name || "",
    thumbnail_url: p.thumbnail_url || (Array.isArray(p.images) ? p.images[0] : "") || "",
    weight: p.weight || "",
    weight_unit: p.weight_unit || "",
    mrp: Number(p.mrp || 0),
    price_snapshot: Number(p.selling_price || p.price || 0),
    variant_id: variantId || null,
  };
};

/**
 * CartProvider — Blinkit-smooth cart with guest checkout support.
 *
 * • Guest (no session): items live in localStorage under `bb_guest_cart_v1`.
 * • Signed-in customer: items live on the server (existing /cart flow).
 * • On sign-in / sign-up, any guest lines are auto-merged into the server cart
 *   (POST /cart/items per line, best-effort) and the local guest cart is cleared.
 *
 * All qty mutations are OPTIMISTIC: local state updates instantly and the API
 * round-trip runs behind. Rapid taps on the same line coalesce (200 ms trailing
 * debounce per line).
 */
export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const pending = useRef(new Map());          // key -> { timer, target, seq }
  const seqRef = useRef(0);
  const mergedForRef = useRef(null);          // user id we've already merged for
  const isGuest = !user || user.role !== "CUSTOMER";

  // ── Load ──────────────────────────────────────────────────────────────
  // For signed-in customers, if a guest cart exists in localStorage we merge
  // it into the server cart FIRST (once per user id) and then fetch, so the
  // customer never sees an intermediate "empty" server state.
  const refresh = useCallback(async () => {
    if (!user || user.role !== "CUSTOMER") {
      const gc = readGuestCart();
      setCart({ ...gc, totals: recomputeGuestTotals(gc.items) });
      return;
    }
    setLoading(true);
    try {
      const gc = readGuestCart();
      if (gc.items.length > 0 && mergedForRef.current !== user.id) {
        mergedForRef.current = user.id;
        for (const it of gc.items) {
          try {
            const body = it.variant_id
              ? { product_id: it.product_id, variant_id: it.variant_id, qty: it.qty }
              : { product_id: it.product_id, qty: it.qty };
            await api.post("/cart/items", body);
          } catch { /* skip failing lines silently */ }
        }
        clearGuestCart();
      }
      const { data } = await api.get("/cart");
      setCart(data);
    } catch { setCart(null); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Server-mode primitives ────────────────────────────────────────────
  const _add = useCallback(async (product_id, qty, variant_id) => {
    const body = variant_id ? { product_id, variant_id, qty } : { product_id, qty };
    const { data } = await api.post("/cart/items", body); return data;
  }, []);
  const _update = useCallback(async (product_id, qty, variant_id) => {
    const url = variant_id
      ? `/cart/items/${product_id}?variant_id=${encodeURIComponent(variant_id)}`
      : `/cart/items/${product_id}`;
    const { data } = await api.patch(url, { qty }); return data;
  }, []);
  const _remove = useCallback(async (product_id, variant_id) => {
    const url = variant_id
      ? `/cart/items/${product_id}?variant_id=${encodeURIComponent(variant_id)}`
      : `/cart/items/${product_id}`;
    const { data } = await api.delete(url); return data;
  }, []);

  // ── Guest-mode line mutations ─────────────────────────────────────────
  const _guestSetQty = useCallback((product_id, target, variant_id, snapshot) => {
    setCart((prev) => {
      const items = prev?.items ? prev.items.slice() : [];
      const idx = items.findIndex((x) => x.product_id === product_id && (x.variant_id || null) === (variant_id || null));
      if (target <= 0) {
        if (idx >= 0) items.splice(idx, 1);
      } else if (idx >= 0) {
        items[idx] = { ...items[idx], qty: target };
      } else {
        // Need snapshot data for new lines
        const snap = snapshot || {};
        items.push({
          product_id, variant_id: variant_id || null, qty: target,
          name: snap.name || "", thumbnail_url: snap.thumbnail_url || "",
          weight: snap.weight || "", weight_unit: snap.weight_unit || "",
          mrp: Number(snap.mrp || 0), price_snapshot: Number(snap.price_snapshot || 0),
        });
      }
      const next = { items, totals: recomputeGuestTotals(items) };
      writeGuestCart(next);
      return next;
    });
  }, []);

  const clear = useCallback(async () => {
    if (isGuest) { clearGuestCart(); setCart(emptyGuestCart()); return emptyGuestCart(); }
    const { data } = await api.post("/cart/clear");
    setCart(data);
    return data;
  }, [isGuest]);

  const add    = useCallback(async (pid, qty = 1, vid = null, snapshot = null) => {
    if (isGuest) { _guestSetQty(pid, qty, vid, snapshot); return; }
    const d = await _add(pid, qty, vid); setCart(d); return d;
  }, [isGuest, _add, _guestSetQty]);
  const update = useCallback(async (pid, qty, vid = null) => {
    if (isGuest) { _guestSetQty(pid, qty, vid); return; }
    const d = await _update(pid, qty, vid); setCart(d); return d;
  }, [isGuest, _update, _guestSetQty]);
  const remove = useCallback(async (pid, vid = null) => {
    if (isGuest) { _guestSetQty(pid, 0, vid); return; }
    const d = await _remove(pid, vid); setCart(d); return d;
  }, [isGuest, _remove, _guestSetQty]);

  const items = cart?.items || [];
  const itemCount = items.reduce((s, it) => s + (it.qty || 0), 0);

  const totals = cart?.totals || {};
  const subtotalFromServer = totals.subtotal;
  const subtotalFallback = items.reduce(
    (s, it) => s + (Number(it.price_snapshot || it.unit_price || it.selling_price || 0) * (it.qty || 0)),
    0,
  );
  const subtotal = typeof subtotalFromServer === "number" ? subtotalFromServer : subtotalFallback;
  const tax = Number(totals.tax || 0);
  const discount = Number(totals.discount || 0);
  const savings = items.reduce(
    (s, it) => s + Math.max(0, (Number(it.mrp || 0) - Number(it.price_snapshot || it.unit_price || it.selling_price || 0))) * (it.qty || 0),
    0,
  );

  const qtyOf = useCallback((productId, variantId = null) => {
    const row = items.find((x) => x.product_id === productId && (x.variant_id || null) === (variantId || null));
    return row?.qty || 0;
  }, [items]);

  /**
   * changeQty — the stepper primitive.
   * Optimistic: mutates local cart in place, then debounces the API call
   * (server mode) or writes to localStorage immediately (guest mode).
   *
   * When called with a `product` snapshot (rich object with name/price/mrp/etc.),
   * new lines in guest mode inherit that data so the Cart page can render
   * without re-fetching each product.
   */
  const changeQty = useCallback(async (productId, delta, variantId = null, product = null) => {
    const key = lineKey(productId, variantId);
    const now = seqRef.current + 1; seqRef.current = now;
    const snap = product ? snapshotFromProduct(product, variantId) : null;

    // ── 1. Compute the target based on current OPTIMISTIC state ─────────
    let computedTarget = 0;
    setCart((prev) => {
      const prevItems = prev?.items || [];
      const idx = prevItems.findIndex((x) => x.product_id === productId && (x.variant_id || null) === (variantId || null));
      const current = idx >= 0 ? (prevItems[idx].qty || 0) : 0;
      const target = Math.max(0, current + delta);
      computedTarget = target;

      // Remember target for the debounced flush (server mode only).
      const p = pending.current.get(key) || {};
      p.target = target;
      p.seq = now;
      pending.current.set(key, p);

      // Build new items array reflecting the target immediately.
      let newItems;
      if (target === 0) {
        newItems = prevItems.filter((_, i) => i !== idx);
      } else if (idx >= 0) {
        newItems = prevItems.slice();
        newItems[idx] = { ...newItems[idx], qty: target };
      } else {
        // Best-effort synthetic line so the button flips to a stepper instantly.
        // In guest mode we carry the full snapshot so the Cart page renders.
        newItems = [...prevItems, {
          product_id: productId, variant_id: variantId, qty: target,
          ...(snap || {}), _optimistic: !isGuest,
        }];
      }
      const nextCart = { ...(prev || {}), items: newItems };
      // Guest: persist immediately + recompute totals so summary widgets stay accurate.
      if (isGuest) {
        nextCart.totals = recomputeGuestTotals(newItems);
        writeGuestCart(nextCart);
      }
      return nextCart;
    });

    // Guest mode is fully local — no API round-trip.
    if (isGuest) return;

    // ── 2. Debounce the API call so rapid taps coalesce (server mode) ───
    const p = pending.current.get(key);
    if (p.timer) clearTimeout(p.timer);
    p.timer = setTimeout(async () => {
      const target = p.target;
      const mySeq = p.seq;
      pending.current.delete(key);
      try {
        const serverCart = cart;
        const serverItems = serverCart?.items || [];
        const idxServer = serverItems.findIndex((x) => x.product_id === productId && (x.variant_id || null) === (variantId || null));
        const currentServer = idxServer >= 0 ? (serverItems[idxServer].qty || 0) : 0;
        let fresh;
        if (currentServer === 0 && target > 0)        fresh = await _add(productId, target, variantId);
        else if (target === 0 && currentServer > 0)   fresh = await _remove(productId, variantId);
        else if (target !== currentServer)            fresh = await _update(productId, target, variantId);
        else return;
        if (seqRef.current === mySeq) setCart(fresh);
      } catch {
        refresh();
      }
    }, 180);
    return computedTarget;
  }, [_add, _update, _remove, cart, refresh, isGuest]);

  const value = useMemo(() => ({
    cart, loading, refresh,
    add, update, remove, clear,
    itemCount, subtotal, tax, discount, savings,
    qtyOf, changeQty, lineKey,
    isGuest,
  }), [cart, loading, refresh, add, update, remove, clear,
       itemCount, subtotal, tax, discount, savings, qtyOf, changeQty, isGuest]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
